import type { CellCompareFunc, ColumnData, ColumnRegular } from '@revolist/react-datagrid';

import { RevoGridBeforeSortingEvent } from './types';

/** Matches RevoGrid's own default: an empty cell sorts first, ascending. */
const isEmptyValue = (value: unknown): boolean =>
  value === '' || value === null || value === undefined;

/**
 * The value as a number, or `null` when it is not one — a numeric string counts
 * ("42" and 42 order the same, and a decimal one orders by value rather than by
 * digit runs, which is where a collator would get 13.54 > 13.6).
 */
const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

/**
 * Default comparer for every sortable column: numbers by value, everything else
 * by natural (number-aware) collation.
 *
 * Reads the raw model value, like RevoGrid's own comparer — a column whose
 * template renders something else has to bring its own `cellCompare`.
 */
export const naturalCellCompare: CellCompareFunc = (prop, a, b) => {
  const aValue = a?.[prop];
  const bValue = b?.[prop];

  if (isEmptyValue(aValue) || isEmptyValue(bValue)) {
    if (isEmptyValue(aValue) && isEmptyValue(bValue)) {
      return 0;
    }
    return isEmptyValue(aValue) ? -1 : 1;
  }

  const aNumber = toNumber(aValue);
  const bNumber = toNumber(bValue);
  if (aNumber !== null && bNumber !== null) {
    return aNumber - bNumber;
  }

  return collator.compare(String(aValue), String(bValue));
};

const toSortableColumn = (column: ColumnRegular): ColumnRegular => {
  if (column.sortable === false) {
    return column;
  }
  return {
    ...column,
    sortable: true,
    cellCompare: column.cellCompare ?? naturalCellCompare,
  };
};

/**
 * Opts every leaf column into click-to-sort with the natural comparer,
 * recursing through the grouping nodes (a group is a header band, not data, so
 * it is never sortable itself — its children are).
 *
 * A column keeps whatever it declares: `sortable: false` to stay out of sorting
 * entirely, or its own `cellCompare` to order by something the default comparer
 * cannot see.
 */
export const withSortableColumns = (columns: ColumnData): ColumnData =>
  columns.map((column) =>
    'children' in column
      ? { ...column, children: withSortableColumns(column.children) }
      : toSortableColumn(column)
  );

/**
 * Keeps the header-click cycle at two states, ascending ⇄ descending.
 *
 * RevoGrid's third click resolves to no order at all, which drops back to source
 * order and leaves the column with no arrow; sending `asc` back in its place is
 * enough, because the plugin reads the order off this event rather than off the
 * value it computed.
 */
export const keepSortDirection = (event: RevoGridBeforeSortingEvent): void => {
  if (event.defaultPrevented || event.detail.order) {
    return;
  }
  event.detail.order = 'asc';
};
